import React from "react";
import { 
  Briefcase, MapPin, CheckCircle2, Clock, Users, Wrench, GraduationCap, 
  Sparkles, ArrowRight, BookOpen, ShieldCheck, HelpCircle, PhoneCall, ChevronRight 
} from "lucide-react";
import { JobCategoryDefinition } from "../../utils/jobCategoryRegistry";
import { getAllQualifiedLocations } from "../../utils/locationRegistry";
import { getAllGuides } from "../../utils/guideRegistry";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";

interface JobCategoryPageProps {
  category: JobCategoryDefinition;
  onNavigate: (path: string) => void;
}

export function JobCategoryPage({ category, onNavigate }: JobCategoryPageProps) {
  const topLocations = getAllQualifiedLocations().slice(0, 18);
  const topGuides = getAllGuides().slice(0, 4);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
      {/* Breadcrumbs */}
      <SeoBreadcrumbs 
        items={[
          { label: "Home", path: "/" },
          { label: "Jobs Directory", path: "/jobs" },
          { label: category.name, path: `/jobs/${category.slug}` }
        ]} 
        onNavigate={onNavigate}
      />

      {/* Hero Banner */}
      <header className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-3xl p-8 sm:p-12 relative overflow-hidden shadow-xs space-y-6">
        <div className="relative z-10 max-w-4xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-900/60 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span>Job Category & Intent Silo</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-950 dark:text-white leading-tight">
            {category.heroHeadline}
          </h1>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed max-w-3xl">
            {category.shortDescription}
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button 
              onClick={() => onNavigate(`/jobs/${category.slug}`)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
            >
              <Briefcase className="w-4 h-4" />
              <span>Search {category.name} Near You</span>
            </button>
            <button 
              onClick={() => onNavigate("/hidden-jobs")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 font-bold text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
            >
              <span>Learn Direct Outreach Strategy</span>
            </button>
          </div>
        </div>
      </header>

      {/* Overview & Common Workplaces */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Key Overview Card */}
          <section className="bg-white dark:bg-slate-800 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 shadow-sm space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
              <span>Role Overview & Expectations</span>
            </h2>

            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/60 space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Entry & Experience Requirements
              </h3>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-relaxed">
                {category.entryRequirements}
              </p>
            </div>

            {/* Daily Responsibilities */}
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Sample Daily Responsibilities
              </h3>
              <ul className="space-y-2.5">
                {category.sampleResponsibilities.map((resp, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{resp}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Valued Skills */}
            <div className="space-y-3 pt-2">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Key Skills Valued by Employers
              </h3>
              <div className="flex flex-wrap gap-2">
                {category.keySkills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-bold">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Interview & Application Tips */}
          <section className="bg-white dark:bg-slate-800 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-700 shadow-sm space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-indigo-600" />
              <span>Interview Preparation & Best Practices</span>
            </h2>

            <ul className="space-y-3">
              {category.interviewTips.map((tip, idx) => (
                <li key={idx} className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 text-sm text-indigo-950 dark:text-indigo-200 leading-relaxed font-medium">
                  • {tip}
                </li>
              ))}
            </ul>

            {/* Direct Outreach Tip */}
            <div className="p-5 rounded-2xl bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/60 space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                <PhoneCall className="w-4 h-4 text-amber-600" />
                <span>Local Business Outreach Tip</span>
              </h3>
              <p className="text-xs sm:text-sm text-amber-900 dark:text-amber-200 leading-relaxed font-medium">
                {category.outreachTip}
              </p>
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {/* Common Workplaces */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span>Common Workplaces</span>
            </h3>
            <ul className="space-y-2">
              {category.commonWorkplaces.map((place, idx) => (
                <li key={idx} className="text-xs font-semibold text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                  {place}
                </li>
              ))}
            </ul>
          </div>

          {/* Related Job Categories */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm space-y-4">
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              Related Job Intent Silos
            </h3>
            <div className="space-y-2">
              {category.relatedCategorySlugs.map(relSlug => (
                <button
                  key={relSlug}
                  onClick={() => onNavigate(`/jobs/${relSlug}`)}
                  className="w-full text-left p-3 rounded-xl bg-slate-50 dark:bg-slate-900 hover:bg-blue-50 dark:hover:bg-blue-950/60 text-xs font-bold text-slate-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 border border-slate-200 dark:border-slate-700 flex items-center justify-between transition-colors"
                >
                  <span className="capitalize">{relSlug.replace(/-/g, " ")}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Top City Hiring Links for this Category */}
      <section className="bg-slate-50 dark:bg-slate-800/50 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-700 space-y-4">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          <span>Find {category.name} in Top Cities</span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {topLocations.map(loc => (
            <button 
              key={loc.citySlug}
              onClick={() => onNavigate(`/jobs/${loc.citySlug}/${category.slug}`)}
              className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition-all truncate"
            >
              {loc.cityName}, {loc.stateAbbreviation}
            </button>
          ))}
        </div>
      </section>

      {/* Helpful Guides */}
      <section className="space-y-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          <span>Recommended Job Search Strategies</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {topGuides.map(g => (
            <div 
              key={g.slug}
              onClick={() => onNavigate(`/guides/${g.slug}`)}
              className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-500 shadow-sm transition-all cursor-pointer space-y-2"
            >
              <h3 className="text-xs font-bold text-slate-900 dark:text-white line-clamp-2">
                {g.title}
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2">
                {g.metaDescription}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
